import { useEffect, useRef, useState } from 'react';
import { UseTooltipVisibilityParams } from '../Tooltip.types';

const INTERACTIVE_CLOSE_DELAY = 150;
const SCROLL_SETTLE_DELAY = 150;

export const useTooltipVisibility = ({
  delay,
  transitionDuration,
  interactive,
}: UseTooltipVisibilityParams) => {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout>();
  const closeDelayRef = useRef<NodeJS.Timeout>();
  const unmountTimeoutRef = useRef<NodeJS.Timeout>();
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const isScrollingRef = useRef(false);
  const hoveredRef = useRef(false);
  const pendingTooltipRef = useRef(false);

  const showTooltip = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    hoveredRef.current = true;

    if (isScrollingRef.current) {
      pendingTooltipRef.current = true;
      return;
    }

    clearTimeout(closeDelayRef.current);
    clearTimeout(unmountTimeoutRef.current);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShouldRender(true);
    }, delay);
  };

  const startHiding = () => {
    setVisible(false);

    unmountTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
    }, transitionDuration);
  };

  const hideTooltip = () => {
    hoveredRef.current = false;
    pendingTooltipRef.current = false;

    clearTimeout(timeoutRef.current);
    clearTimeout(closeDelayRef.current);

    if (!interactive) {
      startHiding();
      return;
    }

    closeDelayRef.current = setTimeout(startHiding, INTERACTIVE_CLOSE_DELAY);
  };

  const keepTooltip = () => {
    clearTimeout(closeDelayRef.current);
  };

  const handleTooltipClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (event.target instanceof Element && event.target.closest('a, button')) {
      hideTooltip();
    }
  };

  const tooltipHandlers = interactive
    ? {
        onMouseEnter: keepTooltip,
        onMouseLeave: hideTooltip,
        onFocus: keepTooltip,
        onBlur: hideTooltip,
        onClick: handleTooltipClick,
      }
    : {};

  const toggleTooltip = () => {
    if (hoveredRef.current) {
      hideTooltip();

      return;
    }

    showTooltip();
  };

  const changeTooltipVisibility = (visible: boolean) => {
    setVisible(visible);
  };

  useEffect(() => {
    const handleScroll = () => {
      isScrollingRef.current = true;
      hideTooltip();

      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;

        if (hoveredRef.current && pendingTooltipRef.current) {
          pendingTooltipRef.current = false;
          showTooltip();
        }
      }, SCROLL_SETTLE_DELAY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeoutRef.current);
      clearTimeout(timeoutRef.current);
      clearTimeout(closeDelayRef.current);
      clearTimeout(unmountTimeoutRef.current);
    };
  }, []);

  return {
    visible,
    shouldRender,
    changeTooltipVisibility,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    tooltipHandlers,
  };
};
